import i18n from '@/utils/i18n';
import mapStyles from './map-styles.json';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { BaseMapViewProps, Region } from './types';
import { createStyles } from '@/contexts/theme-context';
import { GOOGLE_MAPS_LOADER_OPTIONS } from './loader-options';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';

// Map padding in pixels, according to the documentation, although
// Google seems to snap zoom to grid, so this value is a bit arbitrary
const MAP_PADDING = 100;

// Styled to blend with Maps' built-in controls (zoom, fullscreen).
const currentLocationButtonStyle: Partial<CSSStyleDeclaration> = {
  alignItems: 'center',
  background: '#fff',
  border: 'none',
  borderRadius: '8px',
  boxShadow: 'rgba(0, 0, 0, 0.3) 0px 1px 4px -1px',
  cursor: 'pointer',
  display: 'flex',
  height: '40px',
  justifyContent: 'center',
  margin: '10px',
  padding: '0',
  width: '40px',
};

// LocateFixed icon (Lucide), inlined to avoid pulling a React renderer into
// the imperatively-managed Maps control element.
const CURRENT_LOCATION_ICON_SVG = `
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5e5e5e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <line x1="2" y1="12" x2="5" y2="12" />
    <line x1="19" y1="12" x2="22" y2="12" />
    <line x1="12" y1="2" x2="12" y2="5" />
    <line x1="12" y1="19" x2="12" y2="22" />
    <circle cx="12" cy="12" r="7" />
    <circle cx="12" cy="12" r="3" />
  </svg>
`;

// Translate a lat/long delta into a Google Bounds object,
// representing the north, east, south, and west edges of the screen
const createBoundsFromObject = (region: Region) => {
  var northEast = new google.maps.LatLng(
    region.latitude + region.latitudeDelta / 2,
    region.longitude + region.longitudeDelta / 2,
  );
  var southWest = new google.maps.LatLng(
    region.latitude - region.latitudeDelta / 2,
    region.longitude - region.longitudeDelta / 2,
  );

  return new google.maps.LatLngBounds(southWest, northEast);
};

export const BaseMapView: React.FC<BaseMapViewProps> = ({
  animateToRegion,
  children,
  clickableIcons,
  initialRegion,
  interactive,
  onCurrentLocation,
  onPanDrag,
  onPress,
  onRegionChangeComplete,
  onZoomChange,
  panToCoordinate,
  showZoomControl,
}) => {
  const [styles, theme] = useStyles();
  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);
  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

  // Google Maps fires `zoom_changed` for our own `fitBounds` calls too, so we
  // flag programmatic moves and ignore the zoom events they emit; only a
  // user zoom (button/scroll/pinch) should reach `onZoomChange`.
  const programmaticMoveRef = useRef(false);

  const handleZoomChanged = () => {
    if (programmaticMoveRef.current) {
      return;
    }
    onZoomChange?.();
  };

  // Deltas are full-span (edge-to-edge), mirroring react-native-maps.
  const handleIdle = () => {
    // The camera has settled — any programmatic move is done.
    programmaticMoveRef.current = false;
    if (!mapRef || !onRegionChangeComplete) {
      return;
    }
    const bounds = mapRef.getBounds();
    if (!bounds) {
      return;
    }
    const northEast = bounds.getNorthEast();
    const southWest = bounds.getSouthWest();
    const center = bounds.getCenter();
    onRegionChangeComplete({
      latitude: center.lat(),
      latitudeDelta: northEast.lat() - southWest.lat(),
      longitude: center.lng(),
      longitudeDelta: northEast.lng() - southWest.lng(),
    });
  };

  // Stable reference; a fresh object each render would trigger GoogleMap's reactive setCenter and undo fitBounds.
  const center = useMemo(
    () => ({ lat: initialRegion.latitude, lng: initialRegion.longitude }),
    [initialRegion.latitude, initialRegion.longitude],
  );

  useEffect(() => {
    if (mapRef && animateToRegion) {
      programmaticMoveRef.current = true;
      mapRef.fitBounds(createBoundsFromObject(animateToRegion), MAP_PADDING);
    }
  }, [animateToRegion, mapRef]);

  // panTo recenters without refitting, so the zoom level is preserved.
  useEffect(() => {
    if (mapRef && panToCoordinate) {
      mapRef.panTo({
        lat: panToCoordinate.latitude,
        lng: panToCoordinate.longitude,
      });
    }
  }, [panToCoordinate, mapRef]);

  // Recenter button pushed into the Maps control chrome (next to zoom).
  const onCurrentLocationRef = useRef(onCurrentLocation);
  onCurrentLocationRef.current = onCurrentLocation;
  const hasCurrentLocation = !!onCurrentLocation;
  useEffect(() => {
    if (!mapRef || !hasCurrentLocation) {
      return;
    }
    const button = document.createElement('button');
    button.type = 'button';
    button.setAttribute('aria-label', i18n.t('current_location'));
    Object.assign(button.style, currentLocationButtonStyle);
    button.innerHTML = CURRENT_LOCATION_ICON_SVG;
    const handleClick = () => onCurrentLocationRef.current?.();
    button.addEventListener('click', handleClick);
    const controls = mapRef.controls[google.maps.ControlPosition.LEFT_BOTTOM];
    controls.push(button);
    return () => {
      button.removeEventListener('click', handleClick);
      for (let i = 0; i < controls.getLength(); i += 1) {
        if (controls.getAt(i) === button) {
          controls.removeAt(i);
          break;
        }
      }
    };
  }, [mapRef, hasCurrentLocation]);

  return (
    <View style={styles.container}>
      {!isLoaded ? (
        <ActivityIndicator
          accessibilityRole="progressbar"
          accessibilityState={{ busy: true }}
          accessible
          color={`${theme.sk.color.icon.tertiary}bb`}
        />
      ) : (
        <GoogleMap
          center={center}
          mapContainerStyle={styles.mapContainer}
          onClick={onPress}
          // dragend only fires for user-driven drags, matching native's
          // onPanDrag semantics (programmatic moves don't trigger it)
          onDragEnd={onPanDrag}
          onIdle={handleIdle}
          onLoad={(m) => {
            setMapRef(m);
            programmaticMoveRef.current = true;
            m.fitBounds(createBoundsFromObject(initialRegion));
            if (showZoomControl) {
              m.setOptions({
                zoomControl: true,
                zoomControlOptions: {
                  position: google.maps.ControlPosition.LEFT_BOTTOM,
                },
              });
            }
          }}
          onZoomChanged={handleZoomChanged}
          options={{
            // Background color that appears for a split second while the map loads in
            backgroundColor: theme.colors.offWhite,
            clickableIcons: clickableIcons ?? interactive,
            // Hide a handful of UI buttons like zoom, street view, and fullscreen
            // This is purley the UI, we can still pan and zoom without the buttons
            disableDefaultUI: true,
            // Prevent users from moving the map
            draggable: interactive,
            // Prevent user from using keyboard to pan and zoom
            keyboardShortcuts: interactive,
            // Customize what appears on the map
            styles: mapStyles,
          }}
        >
          {children}
        </GoogleMap>
      )}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    alignContent: 'center',
    backgroundColor: theme.colors.offWhite,
    flex: 1,
    justifyContent: 'center',
  },
  mapContainer: {
    flex: 1,
  },
}));
